import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import BranchManager from './components/BranchManager';
import SuperAdmin from './components/SuperAdmin';
import BranchList from './components/BranchList';
import BranchDetails from './components/BranchDetails';
import ErrorBoundary from './components/ErrorBoundary';
import DebugInfo from './components/DebugInfo';
import RouteLogger from './components/RouteLogger';
import NotFoundPage from './components/NotFoundPage';
import './App.css';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>טוען...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/manager" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, loading, isAdmin } = useAuth();

  // Log initial route
  useEffect(() => {
    console.log('=== APP INITIALIZATION ===');
    console.log('Using HashRouter - URLs will have #');
    console.log('Initial Path:', window.location.pathname);
    console.log('Hash:', window.location.hash);
    console.log('Full URL:', window.location.href);
    console.log('Hostname:', window.location.hostname);
    console.log('Protocol:', window.location.protocol);
    console.log('========================');
    console.log('Note: With HashRouter, routes look like: /#/manager');
    console.log('This works even if .htaccess/mod_rewrite is disabled');
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>טוען...</p>
      </div>
    );
  }

  return (
    <>
      <RouteLogger />
      <Routes>
        <Route path="/" element={<BranchList />} /> {/* Main public route - branch list */}
        <Route path="/branches" element={<BranchList />} /> {/* Alternative route to branch list */}
        <Route path="/branch/:slug" element={<BranchDetails />} /> {/* Individual branch page */}
        <Route
          path="/login"
          element={!user ? <Login /> : <Navigate to={isAdmin ? '/admin' : '/manager'} replace />}
        />
        <Route
          path="/manager"
          element={
            !user ? (
              <Login />
            ) : isAdmin ? (
              <ProtectedRoute>
                <SuperAdmin />
              </ProtectedRoute>
            ) : (
              <ProtectedRoute>
                <BranchManager />
              </ProtectedRoute>
            )
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin={true}>
              <SuperAdmin />
            </ProtectedRoute>
          }
        />
        {/* Catch-all route for debugging */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <DebugInfo />
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;